/**
 * Engine E2.3d-ii — client helper for the deterministic-authoring reroute.
 *
 * The "Draft week N" deep-link tries the ENGINE first (POST
 * /api/plans/materialize-week). The BACKEND `engine_authoring` flag controls
 * everything: OFF → the endpoint returns `{ state: 'disabled' }` → we fall back
 * to the current chat-draft flow (so this is a no-op until the flag is flipped).
 * ON → the engine authors + persists the week (domain-safe by construction) →
 * we reroute to the plan view.
 *
 * Only a clean `saved` reroutes; ANY other outcome (disabled / needs_review /
 * save_failed / network error → null) falls back to chat, so the existing
 * LLM-draft path remains the safety net.
 *
 * Pure-ish: `requestEngineDraft` is a thin authed POST; the classifiers are
 * pure + unit-tested.
 */

export interface EngineDraftResult {
  state?: string;
  [key: string]: unknown;
}

type AuthFetch = (url: string, init?: { method?: string; body?: string }) => Promise<Response>;

/**
 * POST the engine-authoring request. Returns the parsed result, or null on a
 * network/parse error (→ caller falls back to chat).
 */
export async function requestEngineDraft(
  fetchAuthed: AuthFetch,
  seasonId: string,
  weekNumber: number,
): Promise<EngineDraftResult | null> {
  try {
    const res = await fetchAuthed('/api/plans/materialize-week', {
      method: 'POST',
      body: JSON.stringify({ seasonId, weekNumber }),
    });
    return (await res.json()) as EngineDraftResult;
  } catch {
    return null;
  }
}

/** The engine cleanly authored + persisted the week → reroute to the plan view. */
export function engineDraftSaved(result: EngineDraftResult | null): boolean {
  return !!result && result.state === 'saved';
}

/**
 * Fall back to the chat-draft flow for ANY non-saved outcome: flag OFF
 * (`disabled`), `needs_review`, `save_failed`, or a null (network) result. This
 * keeps the existing LLM path as the safety net.
 */
export function shouldFallbackToChat(result: EngineDraftResult | null): boolean {
  return !engineDraftSaved(result);
}

/**
 * E2.3d-iii — the coach's narration of the engine-authored week, if the backend
 * supplied one on a saved draft (`narration` is guarded server-side: pinned to
 * the validated plan JSON, with a deterministic fallback). Returns a non-empty
 * trimmed string, or null when absent (e.g. an older backend without iii-b) so
 * the caller can degrade gracefully (navigate straight to the plan).
 */
export function engineDraftNarration(result: EngineDraftResult | null): string | null {
  if (!engineDraftSaved(result)) return null;
  const n = (result as EngineDraftResult).narration;
  return typeof n === 'string' && n.trim().length > 0 ? n.trim() : null;
}

// ── Piece 2 (Phase B) — batch "Draft next N weeks" client ────────────────────

/** One cross-week disclosure from the multiWeekValidator (surfaced verbatim). */
export interface BatchDraftWarning {
  type: string;
  weekNumber: number;
  severity: 'warn' | 'block';
  increasePct: number;
  cap: number;
  message: string;
}

/**
 * The parsed `POST /api/plans/materialize-weeks` result. The backend returns one
 * of: `disabled` (flag off), `noop` (all upcoming weeks drafted), `needs_review`
 * (nothing drafted), or `saved` (≥1 week persisted, possibly `partial`). Only the
 * fields the UI reads are typed; unknown extras pass through.
 */
export interface BatchDraftResult {
  state?: 'disabled' | 'noop' | 'needs_review' | 'saved' | string;
  weeks?: Array<{ weekNumber: number; state: string }>;
  warnings?: BatchDraftWarning[];
  /** The MUST-EXPLAIN copy — surface verbatim to the athlete. */
  explain?: string;
  /** A week failed mid-batch → the safe prefix persisted; `failedWeek` is which. */
  partial?: boolean;
  failedWeek?: number;
  /** A cross-week load spike (>2× cap) — disclose the warnings prominently. */
  blockedSpike?: boolean;
  [key: string]: unknown;
}

/**
 * POST the batch-draft request (mirror of `requestEngineDraft`). Returns the
 * parsed result, or null on a network/parse error (→ caller shows an error state).
 */
export async function requestBatchDraft(
  fetchAuthed: AuthFetch,
  seasonId: string,
  count = 4,
): Promise<BatchDraftResult | null> {
  try {
    const res = await fetchAuthed('/api/plans/materialize-weeks', {
      method: 'POST',
      body: JSON.stringify({ seasonId, count }),
    });
    return (await res.json()) as BatchDraftResult;
  } catch {
    return null;
  }
}

/** The batch persisted ≥1 week (`state === 'saved'`), possibly partially. */
export function batchDraftSaved(result: BatchDraftResult | null): boolean {
  return !!result && result.state === 'saved' && Array.isArray(result.weeks) && result.weeks.length > 0;
}
